import {
  Box,
  Button,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ErrorBoundary,
  Text,
} from "@prismicio/editor-ui"
import {
  forwardRef,
  type ReactElement,
  Suspense,
  useMemo,
  useState,
} from "react"

import { useHasCustomTypes } from "../useHasCustomTypes"

export type FilterType = "customType" | "tag" | "author" | "status"

export interface Filter<T = string> {
  value: T
  type: FilterType
}

interface FilterDropDownProps {
  selectedFilters: Filter[]
  type: FilterType
  onFilterSelected: (newFilter: Filter) => void
  optionsFetcher: () => OptionsFetcherItem[]
}

export type OptionsFetcherItem<T = string> = {
  id: T
  label: string
  key: string
}

export const FilterDropDown = forwardRef<HTMLDivElement, FilterDropDownProps>(
  function FilterDropDown(props, ref) {
    const { selectedFilters, onFilterSelected, optionsFetcher, type } = props

    const [searchTerm, setSearchTerm] = useState("")
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const options = optionsFetcher()

    function toggleFilterOption(value: string) {
      onFilterSelected({ type, value })
      setIsDropdownOpen(false)
      setSearchTerm("")
    }

    function onOpenChange(open: boolean) {
      setIsDropdownOpen(open)
      if (!open) setSearchTerm("")
    }

    // block filters while the onboarding process is active
    // we determine if the onboarding is active by the available custom types
    const hasNoCustomTypes = !useHasCustomTypes()

    return (
      <DropdownMenu open={isDropdownOpen} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger>
          <Button
            invisible
            textWeight="normal"
            color="grey"
            endIcon="arrowDropDown"
            disabled={hasNoCustomTypes}
          >
            {getButtonText(selectedFilters, type)}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          border={false}
          maxHeight={256}
          ref={ref}
        >
          <ComboBox variant="attached">
            <ComboBoxInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder="Search"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <ComboBoxContent>
              <FilterOptionsList
                options={options}
                searchTerm={searchTerm}
                toggleFilterOption={toggleFilterOption}
                selectedFilters={selectedFilters}
              />
            </ComboBoxContent>
          </ComboBox>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  },
)

interface FilterOptionsListProps {
  options: {
    id: string
    label: string
    key: string
  }[]
  searchTerm: string
  selectedFilters: Filter[]
  toggleFilterOption: (filterId: string) => void
}

function FilterOptionsList(props: FilterOptionsListProps) {
  const { options, searchTerm, toggleFilterOption, selectedFilters } = props

  function itemIsSelected(item: string) {
    return selectedFilters?.some((filter) => filter.value === item)
  }

  const filteredOptions = useMemo(() => {
    const searchTermLowerCase = searchTerm.toLowerCase()
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTermLowerCase),
    )
  }, [options, searchTerm])

  return (
    <>
      {!filteredOptions.length && (
        <Text
          color="grey9"
          variant="small"
          sx={{ marginBlock: 8, marginInline: 8 }}
        >
          No results found
        </Text>
      )}
      {filteredOptions.map((option) => {
        return (
          <ComboBoxItem
            key={option.key}
            value={option.id}
            onCheckedChange={() => toggleFilterOption(option.id)}
            checked={itemIsSelected(option.key)}
            iconStyle={{
              size: "small",
              color: "grey11",
            }}
          >
            <Box padding={{ inline: 8, block: 4 }}>
              <Text color="grey12" variant="normal">
                {option.label}
              </Text>
            </Box>
          </ComboBoxItem>
        )
      })}
    </>
  )
}

function getButtonText(filters: Filter[], type: FilterType) {
  const filtersLength = filters?.length

  switch (type) {
    case "customType":
      return filtersLength ? `Type (${filtersLength})` : "Type"
    case "tag":
      return filtersLength ? `Tags (${filtersLength})` : "Tags"
    case "status":
      return filtersLength ? `Status (${filtersLength})` : "Status"
    case "author":
      return filtersLength ? `Authors (${filtersLength})` : "Authors"
  }
}

interface FilterDropDownBoundary {
  children: ReactElement<FilterDropDownProps>
  type: FilterType
  selectedFilters: Filter[]
}

export function FilterDropDownBoundary(props: FilterDropDownBoundary) {
  const { children, type, selectedFilters } = props

  return (
    <ErrorBoundary
      renderError={() => (
        <FilterDropdownFallback selectedFilters={selectedFilters} type={type} />
      )}
    >
      <Suspense
        fallback={
          <FilterDropdownFallback
            selectedFilters={selectedFilters}
            type={type}
          />
        }
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

interface FilterDropdownFallbackProps {
  type: FilterType
  selectedFilters: Filter[]
}

function FilterDropdownFallback(props: FilterDropdownFallbackProps) {
  const { selectedFilters, type } = props

  return (
    <Button
      invisible
      textWeight="normal"
      color="grey"
      endIcon="arrowDropDown"
      disabled
    >
      {getButtonText(selectedFilters, type)}
    </Button>
  )
}
